import dayjs from "dayjs";
import { formatAmountWithoutCurrency } from "./formatCurrency";
import { BankCase, BankFactsBody, BankFactsValues, OrderCandidate } from "../types/evidenceBankCase";

// Mirrors the amount rule in apps/api/modules/evidence/schemas/evidenceBankCaseSchemas.ts.
export const AMOUNT = /^\d{1,7}(\.\d{1,2})?$/;
const CURRENCY = /^[A-Za-z]{3}$/;

export const EMPTY_FACTS: BankFactsValues = {
  bankName: "", disputeReference: "", merchantId: "", reasonCode: "FRAUDULENT", reasonText: "",
  amount: "", currency: "USD", disputedOn: "", respondBy: "",
};

const fromCents = (cents: number): string => (cents / 100).toFixed(2);

export function factsOf(bankCase: BankCase): BankFactsValues {
  return {
    bankName: bankCase.bank_name ?? "",
    disputeReference: bankCase.dispute_reference,
    merchantId: bankCase.merchant_id ?? "",
    reasonCode: bankCase.reason_code,
    reasonText: bankCase.reason_text ?? "",
    amount: fromCents(bankCase.amount),
    currency: bankCase.currency,
    disputedOn: bankCase.disputed_on,
    respondBy: bankCase.respond_by ?? "",
  };
}

// A matched payment is the charge the bank disputes, so its amount is a fair first guess. An order
// found by its number carries the order total, which it is not.
export function factsFromCandidate(candidate: OrderCandidate): BankFactsValues {
  const paid = candidate.matchedOn !== "order_number" && candidate.amount !== null;
  return {
    ...EMPTY_FACTS,
    bankName: candidate.bankName ?? "",
    merchantId: candidate.merchantId ?? "",
    amount: paid ? fromCents(candidate.amount as number) : "",
    currency: candidate.currency ?? EMPTY_FACTS.currency,
  };
}

export function factsBody(values: BankFactsValues): BankFactsBody {
  return {
    bankName: values.bankName.trim() || null,
    disputeReference: values.disputeReference.trim(),
    merchantId: values.merchantId.trim() || null,
    reasonCode: values.reasonCode,
    reasonText: values.reasonText.trim() || null,
    amount: values.amount.trim(),
    currency: values.currency.trim().toUpperCase(),
    disputedOn: values.disputedOn,
    respondBy: values.respondBy || null,
  };
}

export function factsErrors(values: BankFactsValues): Partial<Record<keyof BankFactsValues, string>> {
  const errors: Partial<Record<keyof BankFactsValues, string>> = {};
  const amount = values.amount.trim();
  if (!values.disputeReference.trim()) errors.disputeReference = "Enter the dispute ID";
  if (!AMOUNT.test(amount)) errors.amount = "Enter the amount as 59.99";
  else if (Number(amount) <= 0) errors.amount = "The amount must be more than zero";
  if (!CURRENCY.test(values.currency.trim())) errors.currency = "Enter the currency as USD";
  if (!values.disputedOn) errors.disputedOn = "Enter the date";
  return errors;
}

export const candidateCard = (candidate: OrderCandidate): string | null =>
  (candidate.cardLast4 ? `${candidate.cardBrand ?? "Card"} ending ${candidate.cardLast4}` : null);

/** "59.99 USD"; null when either is unknown. */
export const candidateAmount = (cents: number | null, currency: string | null): string | null =>
  (cents !== null && currency ? `${formatAmountWithoutCurrency(cents, currency)} ${currency}` : null);

export const candidateDate = (day: string): string => dayjs.utc(day).format("MMM D, YYYY");

// A respond-by date is a day in the bank's portal, not an instant: counted in whole days, urgent from two days out.
export function respondByLeft(day: string): { left: string; urgent: boolean } {
  const days = dayjs.utc(day).startOf("day").diff(dayjs.utc().startOf("day"), "day");
  const left = days < 0 ? "passed" : days === 0 ? "today" : days === 1 ? "1 day" : `${days} days`;
  return { left, urgent: days <= 2 };
}
